import { memo, useState, useMemo } from 'react';
import clsx from 'clsx';
import * as ReactDOM from 'react-dom';
import { type Location } from 'react-router';
import { Manager, Popper, Reference } from 'react-popper';
import { type Placement } from '@popperjs/core';
import {
	Grow,
	IconButton,
	ListItemButton,
	type ListItemButtonProps,
	ListItemText,
	Paper,
	styled,
	useTheme
} from '@mui/material';

import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import withRouter from '@fuse/core/withRouter';
import { useDebounce } from '@fuse/hooks';
import { Icon } from 'app/main/components';
import FuseNavItem from '../../FuseNavItem';
import type { FuseNavProps } from '../../types/FuseNavProps';

interface StyledListItemButtonProps extends ListItemButtonProps {
	to?: string;
}

const StyledListItem = styled(ListItemButton)<StyledListItemButtonProps>(({ theme }) => ({
	color: theme.palette.text.primary,
	'&.active, &.active:hover, &.active:focus': {
		backgroundColor: `${theme.palette.secondary.main}!important`,
		color: `${theme.palette.secondary.contrastText}!important`,
		'& .fuse-list-item-text-primary': {
			color: 'inherit'
		},
		'& .fuse-list-item-icon': {
			color: 'inherit'
		}
	},
	'& .fuse-list-item-text': {
		padding: '0 0 0 16px'
	},
	'&.level-0': {
		height: 44,
		borderRadius: 4,
		'&:hover': {
			background: 'transparent'
		}
	}
}));

function isUrlInChildren(parent, url) {
	if (!parent.children) {
		return false;
	}

	for (let i = 0; i < parent.children.length; i += 1) {
		if (parent.children[i].children) {
			if (isUrlInChildren(parent.children[i], url)) {
				return true;
			}
		}

		if (parent.children[i].url === url || url.includes(parent.children[i].url)) {
			return true;
		}
	}

	return false;
}

function FuseNavHorizontalGroup(props: FuseNavProps) {
	const { item, nestedLevel, dense } = props;

	const [opened, setOpened] = useState(false);
	const theme = useTheme();

	const handleToggle = useDebounce(open => {
		setOpened(open);
	}, 150);

	return useMemo(() => {
		let popperPlacement: Placement = 'left';

		if (nestedLevel === 0) {
			popperPlacement = theme.direction === 'ltr' ? 'bottom-start' : 'bottom-end';
		} else {
			popperPlacement = theme.direction === 'ltr' ? 'right' : 'left';
		}

		return (
			<Manager>
				<Reference>
					{({ ref }) => (
						<div ref={ref}>
							<StyledListItem
								className={clsx(
									'fuse-list-item',
									'relative',
									`level-${nestedLevel}`,
									isUrlInChildren(item, props.location?.pathname) && 'active'
								)}
								onMouseEnter={() => handleToggle(true)}
								onMouseLeave={() => handleToggle(false)}
								aria-owns={opened ? 'menu-fuse-list-grow' : undefined}
								aria-haspopup="true"
								component={item.url ? NavLinkAdapter : 'li'}
								to={item.url}
								role="button"
							>
								{item.icon && (
									<Icon
										icon={item.icon}
										color="action"
										className={clsx('fuse-list-item-icon shrink-0 text-16', item.iconClass)}
									/>
								)}

								<ListItemText
									className="fuse-list-item-text"
									primary={item.title}
									classes={{ primary: 'text-13' }}
								/>

								{nestedLevel > 0 && (
									<IconButton
										disableRipple
										className="h-16 w-16 p-0 ltr:ml-4 rtl:mr-4"
										color="inherit"
										size="large"
									>
										<Icon
											icon={
												theme.direction === 'ltr'
													? 'keyboard-arrow-right'
													: 'keyboard-arrow-left'
											}
											className="arrow-icon text-16"
										/>
									</IconButton>
								)}
							</StyledListItem>
						</div>
					)}
				</Reference>
				{ReactDOM.createPortal(
					<Popper
						placement={popperPlacement}
						modifiers={[
							{
								name: 'eventListeners',
								options: {
									resize: opened,
									scroll: opened
								}
							}
						]}
						strategy="fixed"
					>
						{({ ref, style, placement, arrowProps }) =>
							opened && (
								<div
									ref={ref}
									style={{
										...style,
										zIndex: 999 + nestedLevel
									}}
									data-placement={placement}
									className={clsx('z-999', !opened && 'pointer-events-none')}
								>
									<Grow in={opened} id="menu-fuse-list-grow" style={{ transformOrigin: '0 0 0' }}>
										<Paper
											className="rounded-8"
											onMouseEnter={() => handleToggle(true)}
											onMouseLeave={() => handleToggle(false)}
										>
											{item.children && (
												<ul
													className={clsx('popper-navigation-list', dense && 'dense', 'px-0')}
												>
													{item.children.map(_item => (
														<FuseNavItem
															key={_item.id}
															type={`horizontal-${_item.type}`}
															item={_item}
															nestedLevel={nestedLevel}
															dense={dense}
														/>
													))}
												</ul>
											)}
										</Paper>
									</Grow>
								</div>
							)
						}
					</Popper>,
					document.querySelector('#root') as HTMLElement
				)}
			</Manager>
		);
	}, [dense, handleToggle, item, nestedLevel, opened, props.location?.pathname, theme.direction]);
}

const NavHorizontalGroup = withRouter(memo(FuseNavHorizontalGroup));
export default NavHorizontalGroup;
